import Marquee from "@/components/magicui/marquee";
import {
  useFetchTestimonials,
  useGetSocialProofAssetById,
} from "../api/queries";
import { Fragment } from "react";
import { GenericTestimonialCard } from "@/components/testify/card";
import { VariantId } from "@/components/testify/card/presets";
import { MarqueeGalleryCustomProps } from "../api/types";
import { WidgetComponent } from "@/types";
import { Show } from "@/components/atoms/show";
import { LoadFonts } from "@/components/atoms/load-fonts";

export interface Props {
  projectId: string;
  variant: VariantId;
  properties: MarqueeGalleryCustomProps;
}

export function HorizontalMarqueeGallery({
  projectId,
  variant,
  properties,
}: Props) {
  const { data: testimonials, isPending } = useFetchTestimonials({
    projectId,
    published: true,
    type: "text",
  });
  const firstRow = testimonials?.slice(0, testimonials?.length / 2);
  const secondRow = testimonials?.slice(testimonials?.length / 2);
  const scrollSpeed = properties?.scrollSpeed || 60;
  const isMultiColumnEnabled = Number(properties?.columnCount || 2) > 1;

  return (
    <Fragment>
      {isPending ? (
        <div className="loader testify-absolute testify-top-1/2 testify-left-1/2 testify-transform -testify-translate-x-1/2 -testify-translate-y-1/2"></div>
      ) : (
        <div
          className="testify-relative testify-flex testify-w-full testify-flex-col testify-items-center testify-justify-center testify-overflow-hidden testify-rounded-lg"
          style={{ fontFamily: properties.font || "Roboto" }}
        >
          <LoadFonts font={properties.font || "Roboto"} />
          <Marquee
            duration={Number(scrollSpeed)}
            pauseOnHover
            className="testify-h-full"
          >
            {firstRow?.map((testimonial) => (
              <div
                className="testify-h-[350px] testify-px-2"
                key={testimonial.id}
              >
                <GenericTestimonialCard
                  isUsedInParent
                  data={testimonial}
                  variant={variant}
                  wrapperStyles={{
                    height: "100%",
                    margin: "0",
                  }}
                  properties={properties}
                />
              </div>
            ))}
          </Marquee>
          <Show when={isMultiColumnEnabled}>
            <Marquee
              reverse
              pauseOnHover
              duration={Number(scrollSpeed)}
              className="testify-h-full"
            >
              {secondRow?.map((testimonial) => (
                <div
                  className="testify-h-[350px] testify-px-2"
                  key={testimonial.id}
                >
                  <GenericTestimonialCard
                    isUsedInParent
                    data={testimonial}
                    variant={variant}
                    wrapperStyles={{
                      height: "100%",
                      margin: "0",
                    }}
                    properties={properties}
                  />
                </div>
              ))}
            </Marquee>
          </Show>
          <Show when={!!properties?.fadeEdges}>
            <div className="testify-pointer-events-none testify-absolute testify-inset-y-0 testify-left-0 testify-w-1/3 testify-bg-gradient-to-r testify-from-white dark:testify-from-background"></div>
            <div className="testify-pointer-events-none testify-absolute testify-inset-y-0 testify-right-0 testify-w-1/3 testify-bg-gradient-to-l testify-from-white dark:testify-from-background"></div>
          </Show>
        </div>
      )}
    </Fragment>
  );
}

export const HorizontalMarqeeGalleryComponentWidget: WidgetComponent = ({
  widgetId,
}) => {
  const { data, isPending } =
    useGetSocialProofAssetById<MarqueeGalleryCustomProps>({ id: widgetId });

  if (!data || isPending) return;

  return (
    <HorizontalMarqueeGallery
      projectId={data.projectId!}
      properties={data.properties}
      variant={data.properties.cardVariant}
    />
  );
};
